/**
 * @file OrderPage.tsx
 * View existing orders
 *
 * Could have one of three variants:
 *   QuoteRequest - a quote pending
 *   ReviewAndReserve - a quote that has been marked ready for payment by operator (by sending quote or sales automation)
 *   ReservationConfirmation - Reservation
 */
import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { useHistory, useParams } from "react-router-dom";

import { Box, CircularProgress } from "@mui/material";
import GQLQueryStatusIndicator from "components/GQLQueryStatusIndicator";
import { LOAD_REQUEST_QUERY } from "globals/graphql";
import { Request } from "types";
import OrderDetailsPage from "./components/OrderDetailsPage";
import { readQuoteSetupReturn } from "./components/ConfirmReservationForm/quoteSetupReturn";

function OrderPage() {
  // hooks
  const { requestId } = useParams<{ requestId: string }>();
  const history = useHistory();
  const [hasSetupReturnParams] = useState(
    () =>
      new URLSearchParams(window.location.search).has("setup_intent") ||
      new URLSearchParams(window.location.search).has(
        "setup_intent_client_secret"
      )
  );
  const [setupReturn] = useState(readQuoteSetupReturn);
  useEffect(() => {
    if (hasSetupReturnParams)
      history.replace({ ...history.location, search: window.location.search });
  }, [history, hasSetupReturnParams]);

  // queries
  const {
    data: requestData,
    error: requestError,
    refetch: requestRefetch,
    loading: requestLoading,
    previousData,
  } = useQuery(LOAD_REQUEST_QUERY, {
    variables: {
      id: requestId,
    },
  });

  const request: Request =
    requestData?.node?.id === requestId
      ? requestData.node
      : previousData?.node?.id === requestId
        ? previousData.node
        : undefined;

  if (requestLoading && !request) {
    return (
      <Box
        width="100%"
        height="90vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={40} thickness={2} />
      </Box>
    );
  }

  if (requestError && !request) {
    return (
      <Box
        width="100%"
        height="90vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <GQLQueryStatusIndicator
          name="Order"
          data={requestData}
          error={requestError}
          refetch={requestRefetch}
        />
      </Box>
    );
  }

  return (
    <Box width="100%" display="flex" justifyContent="center">
      <Box
        width="100%"
        mx={2}
        display="flex"
        flexDirection="column"
        maxWidth="1140px"
      >
        <OrderDetailsPage
          request={request}
          refetchRequest={requestRefetch}
          quoteSetupReturn={setupReturn || undefined}
        />
      </Box>
    </Box>
  );
}

export default OrderPage;
